﻿/**
 * 邀请码 + 激活码 · API client
 * ------------------------------------------------------------
 * 用户侧 /api/codes（会话鉴权，兑换作用于当前所在租户；邀请人只见码状态不见兑换人身份）；
 * 平台侧 /api/sys/activation-codes|invite-codes|invite-relations|invite-policies（code.view/code.edit）。
 */
import { apiGet, apiSend } from './http-client'

export interface CodePolicyOption {
  id: string
  targetPlanCode: string
  targetPlanName: string
  trialDays: number
  serviceTier: string
  maxCodes: number
}

export interface MyInviteCode {
  id: string
  code: string
  planCode: string
  planName: string
  trialDays: number
  serviceTier: string
  status: 'active' | 'redeemed' | 'revoked' | 'expired'
  expireAt: string
  createdAt: string
}

export interface CodeContext {
  enabled: boolean
  subscription: { planCode: string; planName: string; expireAt: string } | null
  trial: { planCode: string; planName: string; expireAt: string } | null
  codes: MyInviteCode[]
  used: number
  max: number
  policies: CodePolicyOption[]
  serviceTiers: { label: string; value: string }[]
}

export interface RedeemResult {
  kind: 'invite' | 'activation'
  planCode: string
  planName: string
  trialDays: number
  expireAt: string
  serviceTier: string
}

export const inviteCodesApi = {
  context: () => apiGet<CodeContext>('/api/codes/context'),
  generateInvite: (policyId?: string) => apiSend<MyInviteCode>('POST', '/api/codes/invite', { policyId }),
  revokeInvite: (id: string) => apiSend<{ ok: true }>('POST', `/api/codes/invite/${id}/revoke`),
  redeem: (code: string) => apiSend<RedeemResult>('POST', '/api/codes/redeem', { code })
}

/* ========== 平台管理侧 ========== */

export interface ActivationCodeRow {
  id: string
  code: string
  plan_code: string
  plan_name?: string
  trial_days: number
  service_tier: string
  batch_no: string
  bound_tenant_id?: string | null
  bound_tenant_name?: string | null
  status: string
  expire_at: string
  redeemed_at?: string | null
  redeemed_by_name?: string | null
  redeemed_tenant_name?: string | null
  remark?: string | null
  created_at: string
}

export interface InviteCodeAdminRow {
  id: string
  code: string
  status: string
  plan_code: string
  plan_name?: string
  trial_days: number
  service_tier: string
  expire_at: string
  created_at: string
  redeemed_at?: string | null
  owner_person_id: string
  owner_name?: string
  owner_tenant_name?: string
  redeemed_by_name?: string | null
  redeemed_tenant_name?: string | null
}

export interface InviteRelationsResult {
  summary: { generated: number; redeemed: number; rate: number }
  topInviters: { owner_person_id: string; owner_name?: string; generated: number; redeemed: number }[]
  planDist: { plan_code: string; plan_name?: string; count: number }[]
  detail: {
    person_id: string
    person_name?: string
    inviter_person_id: string
    inviter_name?: string
    tenant_id: string
    tenant_name?: string
    created_at: string
    invite_code?: string
    plan_code?: string
  }[]
}

export interface InviteTreeRow {
  person_id: string
  person_name?: string
  inviter_person_id?: string | null
  inviter_name?: string | null
  tenant_id: string
  tenant_name?: string
  depth: number
  created_at: string
}

export interface InvitePolicyRow {
  id: string
  issuer_plan_code: string
  target_plan_code: string
  target_plan_name?: string
  trial_days: number
  service_tier: string
  max_codes?: number | null
  status: string
  created_at?: string
  updated_at?: string
}

export const codeAdminApi = {
  activationCodes: (batchNo?: string, status?: string) =>
    apiGet<ActivationCodeRow[]>('/api/sys/activation-codes', { batch_no: batchNo, status }),
  createActivationBatch: (body: {
    planCode: string
    trialDays: number
    serviceTier: string
    count: number
    expireAt: string
    boundTenantId?: string
    remark?: string
  }) => apiSend<{ batchNo: string; count: number; planName: string }>('POST', '/api/sys/activation-codes', body),
  revokeActivation: (id: string) => apiSend<{ ok: true }>('POST', `/api/sys/activation-codes/${id}/revoke`),
  inviteCodes: (status?: string) => apiGet<InviteCodeAdminRow[]>('/api/sys/invite-codes', { status }),
  revokeInvite: (id: string) => apiSend<{ ok: true }>('POST', `/api/sys/invite-codes/${id}/revoke`),
  inviteRelations: (from?: string, to?: string) => apiGet<InviteRelationsResult>('/api/sys/invite-relations', { from, to }),
  inviteTree: (rootPersonId?: string) => apiGet<InviteTreeRow[]>('/api/sys/invite-relations/tree', { rootPersonId }),
  policies: () => apiGet<InvitePolicyRow[]>('/api/sys/invite-policies'),
  createPolicy: (body: { issuerPlanCode: string; targetPlanCode: string; trialDays: number; serviceTier: string; maxCodes?: number | null }) =>
    apiSend<InvitePolicyRow>('POST', '/api/sys/invite-policies', body),
  updatePolicy: (id: string, body: { trialDays: number; serviceTier: string; maxCodes?: number | null; status: string }) =>
    apiSend<InvitePolicyRow>('PUT', `/api/sys/invite-policies/${id}`, body)
}

/** 按批次导出激活码 CSV（携带会话，浏览器触发下载） */
export async function downloadActivationCsv(batchNo: string): Promise<void> {
  const res = await fetch(`/api/sys/activation-codes/export?batch_no=${encodeURIComponent(batchNo)}`, { credentials: 'same-origin' })
  if (!res.ok) {
    let msg = `HTTP ${res.status}`
    try {
      const j = await res.json()
      if (j?.message) msg = j.message
    } catch { /* 非 JSON 响应 */ }
    throw new Error(msg)
  }
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `activation-codes-${batchNo}.csv`
  a.click()
  URL.revokeObjectURL(url)
}
